import { ContentCopy, PushPin, PushPinOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";

interface MeasureResultFieldProps {
  id: string;
  title: string;
  value: string;
  loggingString: string;
  activeAttributes: string[];
  changeActiveAttributes: (state: string[]) => void;
  showPin: boolean;
}

export default function MeasureResultField({
  id,
  title,
  value,
  loggingString,
  activeAttributes,
  changeActiveAttributes,
  showPin,
}: MeasureResultFieldProps) {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const apm = defaultStore((state) => state.apm);

  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );

  const { t } = useTranslation();

  //copy value from input
  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
    changeAlertMessage({
      message: t("alert_success.copying_of_values"),
      type: "success",
    });
  };

  //pin value for small content
  const pinValue = (value: string) => {
    if (activeAttributes.includes(value)) {
      changeActiveAttributes(
        activeAttributes.filter((item: string) => item !== value)
      );
    } else {
      changeActiveAttributes([...activeAttributes, value]);
    }
  };

  return (
    <Box
      key={title}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "start",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: darkTheme ? "white" : "darkblue.main",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width:
              showSmallToolContent || !showPin ? "100%" : "calc(100% - 34px)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: 1,
            borderRadius: 1,
            gap: 1,
            borderColor: darkTheme ? "white" : "darkblue.main",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 400,
              paddingX: 1,
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {value}
          </Typography>
          <IconButton
            disableRipple
            disableFocusRipple
            onClick={() =>
              monitor(apm, `copy-value | ${loggingString}`, () =>
                copyValue(value)
              )
            }
            size="small"
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          >
            <ContentCopy />
          </IconButton>
        </Box>
        {!showSmallToolContent && showPin && (
          <IconButton
            disableRipple
            disableFocusRipple
            onClick={() =>
              monitor(apm, `change-pin-status | ${loggingString}`, () =>
                pinValue(id)
              )
            }
            size="small"
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          >
            {activeAttributes.includes(id) ? <PushPin /> : <PushPinOutlined />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
